import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import { Page, SectionBlock } from "../components/Page";

/* ─────────────── inline motif components ─────────────── */

function CalligraphicUnderline() {
  return (
    <p
      className="display text-ink"
      style={{ fontSize: "clamp(2.4rem, 5vw, 3.6rem)" }}
    >
      Ten in the{" "}
      <span className="relative inline-block">
        <em className="italic text-amber">evening.</em>
        <svg
          className="pointer-events-none absolute -bottom-2 left-0 w-full"
          viewBox="0 0 600 16"
          preserveAspectRatio="none"
          aria-hidden
        >
          <motion.path
            d="M2 9 C 80 4, 180 13, 260 8 S 460 4, 596 10"
            stroke="var(--amber)"
            strokeWidth="2.2"
            strokeLinecap="round"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.9 }}
            transition={{ duration: 1.6, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
          />
        </svg>
      </span>
    </p>
  );
}

function LeafPetal({
  width = 100,
  rotate = 0,
}: {
  width?: number;
  rotate?: number;
}) {
  return (
    <svg
      viewBox="0 0 130 240"
      width={width}
      height={width * 1.8}
      fill="none"
      style={{ transform: `rotate(${rotate}deg)` }}
      aria-hidden
    >
      <path
        d="M65 0 C 70 60, 60 100, 65 140"
        stroke="var(--amber)"
        strokeWidth="2"
        opacity="0.55"
      />
      <path
        d="M65 140 C 35 160, 30 200, 60 225 C 75 230, 95 215, 100 195 C 105 170, 90 145, 65 140 Z"
        fill="var(--amber)"
        opacity="0.85"
      />
    </svg>
  );
}

function OnlineStamp({
  rotate = -10,
  size = 92,
  label = "online",
}: {
  rotate?: number;
  size?: number;
  label?: string;
}) {
  return (
    <div
      className="flex items-center justify-center rounded-full"
      style={{
        background: "var(--peach-deep)",
        width: size,
        height: size,
        transform: `rotate(${rotate}deg)`,
        boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
      }}
    >
      <span
        className="font-flair font-semibold text-ink"
        style={{ fontSize: size * 0.34 }}
      >
        {label}
      </span>
    </div>
  );
}

function HoverLiftCard() {
  return (
    <motion.a
      href="#"
      onClick={(e) => e.preventDefault()}
      className="surface block p-5"
      style={{ background: "var(--paper)" }}
      initial={{ y: 0 }}
      whileHover={{ y: -4, boxShadow: "0 14px 38px rgba(0,0,0,0.14)" }}
      transition={{ duration: 0.3, ease: [0.2, 0.05, 0.2, 1] }}
    >
      <p className="font-body text-[11px] tracking-[0.32em] uppercase text-amber">
        · Next session
      </p>
      <p className="mt-2 font-display text-[20px] text-ink">
        Meditation for Inner Peace
      </p>
      <p className="mt-1 font-body text-[13px] text-ink-soft">
        Sundays · 10am · Sydney &amp; online
      </p>
      <p className="mt-4 font-body text-[11px] tracking-[0.18em] uppercase text-ink-soft opacity-70">
        Hover →
      </p>
    </motion.a>
  );
}

function TypeCadence({ run }: { run: number }) {
  // re-mount on `run` so the entry sequence plays on demand.
  return (
    <div key={run} className="flex flex-col gap-2">
      <motion.div
        className="flex items-center gap-3"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.0 }}
      >
        <span className="h-px w-10 bg-amber" />
        <span className="eyebrow">Eyebrow</span>
      </motion.div>
      <motion.h3
        className="display text-ink"
        style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)" }}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15, ease: [0.2, 0.05, 0.2, 1] }}
      >
        Ten minutes <em className="italic text-amber">morning.</em>
      </motion.h3>
      <motion.div
        className="h-px w-12 bg-amber"
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        style={{ originX: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      />
      <motion.p
        className="max-w-md font-body text-[15px] text-ink-soft"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
      >
        An ancient meditation, taught carefully — free, and open to anyone.
      </motion.p>
      <motion.span
        className="mt-2 inline-flex rounded-full bg-ink px-4 py-2 font-body text-[12px] font-medium text-paper"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9 }}
        style={{ alignSelf: "flex-start" }}
      >
        Come sit with us
      </motion.span>
    </div>
  );
}

function FloorWarmth() {
  return (
    <div
      className="relative overflow-hidden rounded-2xl"
      style={{ background: "var(--bg)", height: 240 }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, rgba(196,136,78,0.30) 0%, rgba(196,136,78,0.06) 40%, transparent 70%)",
        }}
      />
      <div className="absolute right-5 bottom-4 left-5 flex items-end justify-between">
        <span className="font-body text-[11px] tracking-[0.28em] uppercase text-ink-soft">
          Page floor
        </span>
        <span className="font-flair text-[20px] text-amber">warmth ↑</span>
      </div>
    </div>
  );
}

function FilmGrain() {
  return (
    <div
      className="surface relative h-[200px] overflow-hidden"
      style={{ background: "var(--bg)" }}
    >
      <div
        className="absolute inset-0 mix-blend-multiply opacity-[0.08]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.24 0 0 0 0 0.17 0 0 0 0 0.12 0 0 0 0.9 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          backgroundSize: "220px 220px",
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <p className="font-display text-[28px] italic text-amber">with grain</p>
      </div>
    </div>
  );
}

function HairlineWord() {
  return (
    <div className="flex items-center gap-4">
      <span className="h-px w-12 bg-[color:var(--rule)]" />
      <span className="font-flair text-[26px] text-amber">section</span>
      <span className="h-px flex-1 bg-[color:var(--rule)]" />
    </div>
  );
}

function DevanagariOrnament() {
  // Three glyphs forming an opening invocation — used as a header
  // ornament on long-form documents (the brand book itself, retreat
  // brochures, certificates). Never used decoratively without a
  // Sanskrit + translation pairing nearby.
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-baseline gap-6">
        <span
          className="font-devanagari leading-none"
          style={{ color: "var(--amber)", fontSize: 80 }}
        >
          ॐ
        </span>
        <span
          className="font-devanagari leading-none"
          style={{ color: "var(--amber)", fontSize: 80 }}
        >
          अ
        </span>
        <span
          className="font-devanagari leading-none"
          style={{ color: "var(--amber)", fontSize: 80 }}
        >
          ह
        </span>
      </div>
      <span className="h-px w-40 bg-[color:var(--rule)]" />
      <p className="font-display text-[15px] italic text-ink-soft">
        opening · root · breath
      </p>
    </div>
  );
}

/* ─────────────── page ─────────────── */

export function MotifsAndMotion() {
  const [cadenceRun, setCadenceRun] = useState(0);

  // Replay the type-cadence demo every 6 seconds so it's always
  // showing motion on this page.
  useEffect(() => {
    const id = setInterval(() => setCadenceRun((n) => n + 1), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <Page
      number="07"
      title="Motifs & motion"
      eyebrow="The small details"
      intro={
        <>
          Beyond type and colour, a vocabulary of quiet visual gestures defines
          our surfaces — calligraphic underlines, hairline rules, soft leaves, a
          film grain, a hand-stamped badge, the floor warmth that anchors every
          page. Motion principles round it out: slow, gentle, never bouncy.
        </>
      }
    >
      <SectionBlock title="Calligraphic underline">
        <div
          className="surface flex flex-col items-center gap-6 px-7 py-14"
          style={{ background: "var(--paper)" }}
        >
          <CalligraphicUnderline />
          <p className="max-w-md text-center font-body text-[14px] leading-relaxed text-ink-soft">
            Hand-drawn SVG underline that draws itself in over 1.6 seconds. Only
            ever paired with the italic emphasis word in a headline. Never more
            than once per page.
          </p>
        </div>
      </SectionBlock>

      <SectionBlock title="Leaf petal — solo">
        <div className="grid gap-6 md:grid-cols-3">
          <div
            className="surface flex h-[260px] items-center justify-center"
            style={{ background: "var(--paper)" }}
          >
            <LeafPetal width={90} />
          </div>
          <div className="md:col-span-2">
            <p className="font-display text-[24px] leading-snug text-ink">
              A single leaf, falling. The default. Anchors the top-left of
              campaign posters, brand book section corners, and other
              compositions that want a single quiet ornament.
            </p>
            <p className="mt-4 font-body text-[14px] leading-relaxed text-ink-soft">
              Always at amber, 85% opacity. Stem optional. Never recoloured
              outside amber.
            </p>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Leaf — paired compositions">
        <div className="grid gap-4 md:grid-cols-3">
          <div
            className="surface flex h-[220px] items-center justify-center gap-4"
            style={{ background: "var(--paper)" }}
          >
            <LeafPetal width={60} rotate={-12} />
            <LeafPetal width={50} rotate={8} />
          </div>
          <div
            className="surface relative h-[220px] overflow-hidden"
            style={{ background: "var(--paper)" }}
          >
            <div className="absolute top-3 left-3">
              <LeafPetal width={50} rotate={-8} />
            </div>
            <div className="absolute right-3 bottom-3">
              <LeafPetal width={50} rotate={172} />
            </div>
          </div>
          <div
            className="surface relative h-[220px] overflow-hidden"
            style={{ background: "var(--paper)" }}
          >
            <div className="absolute top-3 right-3 -rotate-12 opacity-60">
              <LeafPetal width={70} />
            </div>
          </div>
        </div>
        <p className="mt-4 max-w-[640px] font-body text-[14px] leading-relaxed text-ink-soft">
          Pair compositions for documents and decks; corner-anchor for posters;
          oversized corner-flare for brochure covers. Never more than three
          leaves on a single surface.
        </p>
      </SectionBlock>

      <SectionBlock title="Hand-stamped badge">
        <div
          className="surface flex flex-col items-center gap-10 px-7 py-12 md:flex-row md:justify-around"
          style={{ background: "var(--paper)" }}
        >
          <OnlineStamp />
          <OnlineStamp size={92} rotate={6} label="free" />
          <OnlineStamp size={92} rotate={-4} label="join" />
        </div>
        <p className="mt-4 max-w-[640px] font-body text-[14px] leading-relaxed text-ink-soft">
          A hand-stamped peach disc with Caveat lettering. Used to call
          attention to a single fact on a surface (the session is online, the
          practice is free, doors are open). Always rotated ±4–10°. One per
          composition.
        </p>
      </SectionBlock>

      <SectionBlock title="Hairline rules & section breaks">
        <div className="flex flex-col gap-4">
          <div className="surface p-6" style={{ background: "var(--paper)" }}>
            <p className="eyebrow">Eyebrow + section rule</p>
            <span className="hairline" />
            <p className="mt-5 font-body text-[14px] leading-relaxed text-ink-soft">
              A 40px wide, 2px tall amber hairline sits below each section
              eyebrow.
            </p>
          </div>
          <div className="surface p-6" style={{ background: "var(--paper)" }}>
            <HairlineWord />
            <p className="mt-5 font-body text-[14px] leading-relaxed text-ink-soft">
              For mid-page section breaks: a flair word flanked by long rules.
            </p>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Floor warmth">
        <div className="grid gap-4 md:grid-cols-2">
          <FloorWarmth />
          <div
            className="surface flex flex-col justify-center p-7"
            style={{ background: "var(--paper)" }}
          >
            <p className="eyebrow">What it does</p>
            <span className="hairline" />
            <p className="mt-5 max-w-md font-body text-[14px] leading-relaxed text-ink-soft">
              A low amber gradient bleeding up from the page floor. Used on
              every full-bleed surface — hero, ad templates, social posts. Holds
              the room together. Reaches about 40% up the surface; fades to
              transparent.
            </p>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Film grain">
        <div className="grid gap-6 md:grid-cols-2">
          <FilmGrain />
          <div
            className="surface flex h-[200px] items-center justify-center"
            style={{ background: "var(--bg)" }}
          >
            <p className="font-display text-[28px] italic text-ink-soft">
              without grain
            </p>
          </div>
        </div>
        <p className="mt-5 max-w-[640px] font-body text-[14px] leading-relaxed text-ink-soft">
          A 6–8% multiply grain over hero areas. It gives the brand a paper feel
          without obvious texture. Apply on top of photo washes; never on
          text-only surfaces.
        </p>
      </SectionBlock>

      <SectionBlock title="Devanagari ornament">
        <div
          className="surface flex flex-col items-center px-7 py-14"
          style={{ background: "var(--paper)" }}
        >
          <DevanagariOrnament />
        </div>
        <p className="mt-4 max-w-[640px] font-body text-[14px] leading-relaxed text-ink-soft">
          Reserved for long-form, ceremonial contexts — the brand book itself,
          retreat brochures, certificates. Always paired with a translation in
          display italic. Never used as a generic decoration.
        </p>
      </SectionBlock>

      <SectionBlock title="Card hover — the lift">
        <div className="grid gap-6 md:grid-cols-2">
          <HoverLiftCard />
          <div
            className="surface flex flex-col justify-center p-7"
            style={{ background: "var(--paper)" }}
          >
            <p className="eyebrow">The move</p>
            <span className="hairline" />
            <p className="mt-5 max-w-md font-body text-[14px] leading-relaxed text-ink-soft">
              On hover, cards lift 4px and gain a slightly heavier shadow over
              300ms. No scale, no rotate, no colour change. The lift is the only
              signal; the rest stays still.
            </p>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Type entry cadence">
        <div
          className="surface flex flex-col gap-6 p-8"
          style={{ background: "var(--paper)" }}
        >
          <AnimatePresence mode="wait">
            <TypeCadence key={cadenceRun} run={cadenceRun} />
          </AnimatePresence>
          <p className="max-w-md border-t pt-4 font-body text-[13px] leading-relaxed text-ink-soft border-rule">
            Eyebrow → headline → rule → sub → CTA. Each element appears
            150–200ms after the last. Cubic-bezier (0.2, 0.05, 0.2, 1). Re-plays
            automatically every six seconds.
          </p>
        </div>
      </SectionBlock>

      <SectionBlock title="Motion principles">
        <div className="grid gap-5 md:grid-cols-2">
          {[
            [
              "Easing is gentle, not bouncy",
              "Cubic-bezier (0.2, 0.05, 0.2, 1) for entry; (0.4, 0, 0.2, 1) for SVG paths. We never use spring overshoots.",
            ],
            [
              "Duration",
              "0.55–0.9s for type entries. 1.2–1.8s for SVG underlines. 30s for ken-burns. Nothing is fast.",
            ],
            [
              "Cadence",
              "Eyebrow → headline → rule → sub → CTA → card. 150–200ms between elements. Always the same order.",
            ],
            [
              "Scroll",
              "Lenis smooth scrolling, gentle parallax (≤12% travel) on hero video. Avoid scroll-jacking.",
            ],
            [
              "Hover",
              "1px lift (-translate-y-0.5) on cards, subtle colour shift on text links. We don't scale, rotate, or jiggle.",
            ],
            [
              "No infinite loops",
              "Everything resolves. The animate-ping next-session dot is the only ongoing loop, intentionally small.",
            ],
          ].map(([t, b]) => (
            <div
              key={t}
              className="border-l-2 pl-5"
              style={{ borderColor: "var(--amber)" }}
            >
              <p className="font-display text-[20px] text-ink">{t}</p>
              <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-soft">
                {b}
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>
    </Page>
  );
}
